import Script from 'next/script';
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';

declare global {
  interface Window {
    fbq: any;
  }
}

type MetaPixelProps = {
  pixelId: string;
  initEvent?: boolean;
};

export interface MetaPixelRef {
  trackCheckoutSuccess: (data?: any) => void;
  trackCheckoutInitiated: (data?: any) => void;
}

const MetaPixel = forwardRef<MetaPixelRef, MetaPixelProps>(
  ({ pixelId, initEvent = true }, ref) => {
    const [loaded, setLoaded] = useState(!!window.fbq);

    useImperativeHandle(ref, () => ({
      trackCheckoutSuccess: (data = {}) => {
        if (window.fbq && window.fbq?.instance?.pixelsByID[pixelId]) {
          window.fbq('trackSingleCustom', pixelId, 'CheckoutSuccess', {
            eventId: data.eventId,
            eventName: data.eventName,
            value: data.value
          });
        }
      },
      trackCheckoutInitiated: (data = {}) => {
        if (window.fbq && window.fbq?.instance?.pixelsByID[pixelId]) {
          window.fbq('trackSingleCustom', pixelId, 'CheckoutInitiated', {
            eventId: data.eventId,
            eventName: data.eventName
          });
        }
      }
    }));

    useEffect(() => {
      if (!loaded) {
        return;
      }

      // Check if the Meta Pixel has already been registered.
      // If not, register it before sending the PageView event for that specific pixel.
      if (!window.fbq?.instance?.pixelsByID[pixelId]) {
        window.fbq.disablePushState = true;
        window.fbq('set', 'autoConfig', false, pixelId);
        window.fbq('init', pixelId);
      }

      if (initEvent) {
        window.fbq('trackSingleCustom', pixelId, 'PageView');
      }
    }, [loaded]);

    // TODO: If we make this evolve to fire other events, it will make more sense to change this implementation.
    // Move the script loading to a context provider (probably just wrapping the event page).
    // The attached context should provide the required methods to fire events for a specific pixel.
    // All parts of the event and subcomponents can now access and fire Meta related events.
    return (
      <Script
        id="fb-pixel"
        src="/meta-pixel.js"
        strategy="afterInteractive"
        onLoad={() => setLoaded(true)}
      />
    );
  }
);

export default MetaPixel;
